import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faMinus, faSpinner } from '@fortawesome/free-solid-svg-icons';

import stockpileService from '../../services/stockpileService';
import { DatePicker } from '../ui';
import AmountInput from '../ui/AmountInput';
import './StockMovementModal.css';

const todayIso = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

// Convert a YYYY-MM-DD picked by the user into a UTC ISO timestamp anchored at
// 12:00 local time. Anchoring at midday avoids timezone rollover that would
// otherwise drag the entry into the previous day in some zones.
const dateOnlyToIso = (yyyyMmDd) => {
    if (!yyyyMmDd) return null;
    const [y, m, d] = yyyyMmDd.split('-').map(Number);
    if (!y || !m || !d) return null;
    const local = new Date(y, m - 1, d, 12, 0, 0, 0);
    return local.toISOString();
};

const StockMovementModal = ({
    isOpen,
    movementType,
    variantId,
    unitDisplay,
    onClose,
    onSuccess,
}) => {
    const [quantity, setQuantity] = useState('');
    const [occurredOn, setOccurredOn] = useState(todayIso());
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setQuantity('');
            setOccurredOn(todayIso());
            setNotes('');
            setError(null);
            setSubmitting(false);
        }
    }, [isOpen, movementType]);

    if (!isOpen) return null;

    const isInbound = movementType === 'inbound';
    const titleVerb = isInbound ? 'Record inbound stock' : 'Record outbound stock';
    const submitVerb = isInbound ? 'Add to stockpile' : 'Remove from stockpile';

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmed = quantity.trim();
        if (!trimmed) {
            setError('Enter a quantity.');
            return;
        }
        const numeric = Number(trimmed);
        if (Number.isNaN(numeric) || numeric <= 0) {
            setError('Quantity must be a positive number.');
            return;
        }

        const occurredIso = dateOnlyToIso(occurredOn);
        if (!occurredIso) {
            setError('Pick a valid date.');
            return;
        }

        try {
            setSubmitting(true);
            setError(null);
            await stockpileService.postMovement(variantId, {
                movement_type: movementType,
                delta: trimmed,
                occurred_at: occurredIso,
                notes: notes.trim(),
            });
            onSuccess?.();
        } catch (err) {
            console.error('Stockpile movement failed', err);
            const detail = err?.response?.data;
            const detailString =
                typeof detail === 'string'
                    ? detail
                    : detail?.detail || detail?.error || JSON.stringify(detail || {});
            setError(detailString || 'Could not save movement.');
        } finally {
            setSubmitting(false);
        }
    };

    return ReactDOM.createPortal(
        <div className="stockpile-modal-overlay" onClick={onClose}>
            <div className="stockpile-modal" onClick={(e) => e.stopPropagation()}>
                <div className={`stockpile-modal-header${isInbound ? '' : ' outbound'}`}>
                    <h3 className="stockpile-modal-title">
                        <FontAwesomeIcon icon={isInbound ? faPlus : faMinus} />
                        {titleVerb}
                    </h3>
                    <button
                        type="button"
                        className="stockpile-modal-close"
                        onClick={onClose}
                        disabled={submitting}
                        aria-label="Close"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <form className="hms-stacked-form" onSubmit={handleSubmit}>
                    <div className="stockpile-modal-body">
                        <div className="stockpile-field">
                            <label className="stockpile-field-label">Quantity</label>
                            <div className="stockpile-quantity-input">
                                <AmountInput
                                    value={quantity}
                                    onChange={setQuantity}
                                    autoFocus
                                    placeholder="0.00"
                                />
                                <span className="stockpile-quantity-unit">{unitDisplay || 'unit'}</span>
                            </div>
                        </div>

                        <div className="stockpile-field">
                            <label className="stockpile-field-label">When did this happen?</label>
                            <DatePicker
                                name="occurred_on"
                                value={occurredOn}
                                onChange={(e) => setOccurredOn(e.target.value || '')}
                                format="YYYY-MM-DD"
                                placeholder="Select date"
                            />
                        </div>

                        <div className="stockpile-field">
                            <label className="stockpile-field-label">Notes (optional)</label>
                            <textarea
                                className="stockpile-textarea"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Reference, BL number, reason for the manual entry…"
                                maxLength={2000}
                            />
                        </div>

                        {error && <div className="stockpile-modal-error">{error}</div>}
                    </div>

                    <div className="stockpile-modal-footer">
                        <button
                            type="button"
                            className="stockpile-modal-btn secondary"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={`stockpile-modal-btn ${isInbound ? 'primary' : 'danger'}`}
                            disabled={submitting}
                        >
                            {submitting && <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.4rem' }} />}
                            {submitVerb}
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};

export default StockMovementModal;
